import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiCreditCard,
  FiLock,
  FiShield
} from 'react-icons/fi';
import { API_BASE } from '../../config/api';
import { useLocation, useNavigate } from 'react-router-dom';
import useCart from '../../hooks/useCart';
import './CheckoutStyles.css';

const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const CashfreePayment = ({
  totalAmount,
  itemsPrice = 0,
  shippingPrice = 0,
  taxPrice = 0,
  order,
  onSuccess,
  onError,
  onBack,
  selectedPaymentMethod
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cashfreeKey, setCashfreeKey] = useState('');
  const [cashfreeMode, setCashfreeMode] = useState('sandbox');
  const [cashfreeLoaded, setCashfreeLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const isUserDroppedPayment = (errorLike) => {
    const raw = `${errorLike?.code || ''} ${errorLike?.type || ''} ${errorLike?.message || ''}`.toLowerCase();
    return raw.includes('user_dropped') ||
      raw.includes('dropped') ||
      raw.includes('closed') ||
      raw.includes('cancelled') ||
      raw.includes('canceled') ||
      raw.includes('not completed');
  };

  const handlePaymentOutcomeFailure = async ({
    paymentState = 'failed',
    message,
    cashfreeStatus,
    rawError
  }) => {
    const finalMessage = message || (
      paymentState === 'incomplete'
        ? 'Payment not completed. Please proceed again.'
        : 'Payment failed. Please try again.'
    );

    setError(finalMessage);
    setLoading(false);

    if (order && order._id) {
      await markOrderAsPaymentFailed(order._id, finalMessage, paymentState, cashfreeStatus);
    }

    if (onError) {
      onError({
        paymentState,
        message: finalMessage,
        rawError
      });
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const orderId = queryParams.get('order_id');

    if (orderId) {
      verifyPayment(orderId);
    }
  }, [location]);

  useEffect(() => {
    const loadCashfreeScript = () => (
      new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.onload = () => {
          setCashfreeLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          setError('Payment system failed to load. Please refresh the page.');
          resolve(false);
        };
        document.body.appendChild(script);
      })
    );

    if (window.Cashfree) {
      setCashfreeLoaded(true);
    } else {
      loadCashfreeScript();
    }
  }, []);

  useEffect(() => {
    const fetchCashfreeKey = async () => {
      try {
        const response = await axios.get(`${API_BASE}/v1/payment/cashfree-key`, {
          withCredentials: true
        });

        if (response.data.success) {
          setCashfreeKey(response.data.key);
          setCashfreeMode(response.data.mode || 'sandbox');
        } else {
          setError('Could not initialize payment system. Please try again later.');
        }
      } catch (fetchError) {
        setError('Could not initialize payment system. Please try again later.');
      }
    };

    fetchCashfreeKey();
  }, []);

  const handlePayment = async () => {
    if (!window.Cashfree || !cashfreeKey) {
      setError('Payment system is not available. Please refresh the page and try again.');
      return;
    }

    if (!order || !order._id) {
      setError('Order not found. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const orderResponse = await axios.post(
        `${API_BASE}/v1/payment/cashfree/create-order`,
        {
          amount: totalAmount,
          currency: 'INR',
          order_id: order._id,
          customer_details: {
            customer_id: `customer_${order._id}`,
            customer_phone: order.shippingAddress?.phone || '9999999999',
            customer_email: order.shippingAddress?.email || 'customer@example.com',
            customer_name: order.shippingAddress?.fullName || 'Test Customer'
          }
        },
        {
          withCredentials: true
        }
      );

      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.message || 'Could not create payment session');
      }

      const cashfreeOrder = orderResponse.data.order;
      const cashfree = new window.Cashfree({
        mode: cashfreeMode,
      });

      const dropinContainer = document.getElementById('cashfree-dropin');
      if (dropinContainer) {
        dropinContainer.innerHTML = '';
      }

      const checkoutOptions = {
        paymentSessionId: cashfreeOrder.payment_session_id,
        redirectTarget: '_self',
      };

      cashfree.checkout(checkoutOptions).then((result) => {
        if (result.error) {
          const paymentState = isUserDroppedPayment(result.error) ? 'incomplete' : 'failed';
          handlePaymentOutcomeFailure({
            paymentState,
            message: paymentState === 'incomplete'
              ? 'Payment not completed. Please proceed again.'
              : (result.error.message || 'Payment failed. Please try again.'),
            cashfreeStatus: result.error.code || result.error.type,
            rawError: result.error
          });
        }
      }).catch((checkoutError) => {
        const paymentState = isUserDroppedPayment(checkoutError) ? 'incomplete' : 'failed';
        handlePaymentOutcomeFailure({
          paymentState,
          message: paymentState === 'incomplete'
            ? 'Payment not completed. Please proceed again.'
            : 'Payment failed. Please try again.',
          cashfreeStatus: checkoutError.code || checkoutError.type,
          rawError: checkoutError
        });
      });
    } catch (paymentError) {
      setError(paymentError.message || 'Payment initialization failed. Please try again.');
      setLoading(false);
    }
  };

  const verifyPayment = async (orderId) => {
    setLoading(true);

    try {
      const verifyResponse = await axios.post(
        `${API_BASE}/v1/payment/verify-payment`,
        { order_id: orderId },
        { withCredentials: true }
      );

      if (verifyResponse.data.success) {
        sessionStorage.setItem('lastOrder', JSON.stringify(verifyResponse.data.order));

        const paymentMethod = {
          id: 'cashfree',
          name: 'Online Payment'
        };
        sessionStorage.setItem('lastPaymentMethod', JSON.stringify(paymentMethod));

        clearCart();

        if (onSuccess) {
          onSuccess(verifyResponse.data.order, paymentMethod);
        } else {
          navigate('/order-confirmation', {
            state: {
              order: verifyResponse.data.order,
              paymentMethod
            }
          });
        }
      } else {
        await handlePaymentOutcomeFailure({
          paymentState: verifyResponse.data.paymentState || 'failed',
          message: verifyResponse.data.message,
          cashfreeStatus: verifyResponse.data.cashfreeStatus,
          rawError: verifyResponse.data
        });
      }
    } catch (verifyError) {
      await handlePaymentOutcomeFailure({
        paymentState: 'failed',
        message: 'Error verifying payment. Please contact support.',
        rawError: verifyError
      });
    }
  };

  const markOrderAsPaymentFailed = async (
    orderId,
    failureReason,
    paymentState = 'failed',
    cashfreeStatus = 'FAILED'
  ) => {
    try {
      await axios.put(
        `${API_BASE}/${orderId}/payment-failed`,
        {
          failure_reason: failureReason,
          statusType: paymentState,
          payment_status: cashfreeStatus,
          cashfree_order_id: order?.cashfreeOrderId || order?.paymentResult?.id || orderId
        },
        { withCredentials: true }
      );
    } catch (markFailedError) {
      console.error('Failed to mark order as payment failed:', markFailedError);
    }
  };

  return (
    <div className="cashfree-payment-container payment-stage-panel payment-stage-panel--online">
      <div className="payment-stage-intro">
        <div>
          <span className="payment-stage-badge payment-stage-badge--success">Online Payment</span>
          <h3>Payment</h3>
          <p>
            Continue to the secure Cashfree checkout to finish your payment.
            The payment amount and selected method are ready below.
          </p>
        </div>

        <div className="cashfree-payment-badges">
          <span className="payment-summary-method-pill">{selectedPaymentMethod?.name || 'Online Payment'}</span>
          <span className="payment-summary-method-pill payment-method-status-pill--success">
            <FiLock />
            Secure
          </span>
        </div>
      </div>

      {error ? (
        <div className="payment-error">
          <div className="payment-error-content">
            <FiAlertTriangle />
            <div>
              <strong>Payment issue</strong>
              <p>{error}</p>
            </div>
          </div>
          <button type="button" onClick={() => setError('')} className="dismiss-button">Dismiss</button>
        </div>
      ) : null}

      {loading ? (
        <div className="payment-loading">
          <div className="payment-loader"></div>
          <p>Processing your payment...</p>
        </div>
      ) : (
        <>
          <div className="payment-stage-grid">
            <div className="payment-stage-detail-card payment-stage-detail-card--primary">
              <span className="payment-icon-shell">
                <FiCreditCard />
              </span>
              <div>
                <span className="payment-stage-label">Selected payment flow</span>
                <h4>Cards, UPI, Netbanking</h4>
                <p>Use your preferred payment option inside the secure Cashfree window.</p>
                <div className="payment-method-logos">
                  <span className="payment-logo visa">Visa</span>
                  <span className="payment-logo mastercard">Mastercard</span>
                  <span className="payment-logo upi">UPI</span>
                  <span className="payment-logo netbanking">Netbanking</span>
                </div>
              </div>
            </div>

            <div className="payment-stage-detail-card">
              <span className="payment-stage-label">Amount to pay</span>
              <strong className="payment-stage-amount">{formatCurrency(totalAmount)}</strong>
              <div className="payment-stage-mini-list">
                <div className="payment-stage-mini-row">
                  <span>Items total</span>
                  <strong>{formatCurrency(order?.itemsPrice ?? itemsPrice)}</strong>
                </div>
                <div className="payment-stage-mini-row">
                  <span>Delivery</span>
                  <strong>{formatCurrency(order?.shippingPrice ?? shippingPrice)}</strong>
                </div>
                {(order?.taxPrice ?? taxPrice) > 0 ? (
                  <div className="payment-stage-mini-row">
                    <span>Taxes</span>
                    <strong>{formatCurrency(order?.taxPrice ?? taxPrice)}</strong>
                  </div>
                ) : null}
                <div className="payment-stage-mini-row">
                  <span>Order reference</span>
                  <strong>{order?._id ? String(order._id).slice(-8).toUpperCase() : 'Pending'}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="payment-stage-note">
            <FiShield />
            <p>You will be redirected securely and brought back automatically after the payment is completed.</p>
          </div>

          <div className="payment-stage-actions">
            <button type="button" onClick={onBack} className="back-button">Change Method</button>
            <button
              type="button"
              onClick={handlePayment}
              className="pay-button"
              disabled={loading || !cashfreeLoaded}
            >
              Pay Now
            </button>
          </div>
        </>
      )}

      <div id="cashfree-dropin" className="cashfree-dropin-anchor"></div>
    </div>
  );
};

export default CashfreePayment;
